import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import ErrorBanner from "../ui/ErrorBanner";
import Spinner from "../ui/Spinner";
import { consumeRedirectAfterAuth } from "../utils/redirectAfterAuth";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login({ email, password });

      // Consume the intended destination (e.g. a shared /n/:id link) on success
      // so it can't hijack a later login; default to /home.
      navigate(consumeRedirectAfterAuth() || "/home", { replace: true });
    } catch (error) {
      setError(error.message || "Login failed. Please check your credentials");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-elevated px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <a href="/" className="text-3xl font-bold text-text-primary">
            Traces
          </a>
          <p className="text-sm text-text-muted mt-1">Welcome back</p>
        </div>

        <form className="space-y-6" onSubmit={handleLogin}>
          {error && (
            <ErrorBanner
              message={error}
              onRetry={() => handleLogin({ preventDefault: () => {} })}
              onDismiss={() => setError(null)}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Email
            </label>
            <input
              type="email"
              className="w-full bg-surface-elevated border border-border-strong rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-text-primary focus:border-text-primary disabled:opacity-50"
              placeholder="johndoe@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full bg-surface-elevated border border-border-strong rounded-md px-3 py-2 pr-10 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-text-primary focus:border-text-primary disabled:opacity-50"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 right-2 -translate-y-1/2 h-8 w-8 inline-grid place-items-center rounded-full text-text-muted hover:text-text-primary transition focus:outline-none"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-text-inverse py-2 px-4 rounded-md text-sm hover:bg-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Spinner size={16} />}
            {submitting ? "Logging in..." : "Log In"}
          </button>

          <div className="text-center text-sm text-text-secondary">
            <a href="/forgot-password" className="hover:underline">
              Forgot your password?
            </a>
          </div>
        </form>

        <div className="text-center text-sm text-text-secondary mt-6">
          Don't have an account?{" "}
          <Link
            to="/signup"
            state={location.state}
            className="text-text-primary hover:underline"
          >
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
